import { redirect } from "@tanstack/react-router";
import { idsCompaniesTrazabilitySection } from "./ConstCapture";
import {
  IDS_TRAZABILITY_FLOW_ADVALTECH_PROD,
  IDS_TRAZABILITY_FLOW_BAFAR_PROD
} from "../ConstApp";

/**
 * Extract dashboard-related state from the Redux store.
 */
export function getDashboardState(store) {
  const state = store.getState();
  return {
    workstationSelected: state.dashboard.workstationSelected,
    workstation: state.dashboard.workstation,
    changePart: state.dashboard.changePart,
    omitPart: state.dashboard.omitPart,
    data: state.dashboard.data
  };
}

/**
 * Read company from React Query cache.
 */
export function getCompany(queryClient) {
  const userData = queryClient.getQueryData(["auth", "userData"]);
  return userData?.company ?? null;
}

/**
 * Check if the current workstation has an active part.
 */
function computeHasActivePart({
  workstationSelected,
  workstation,
  omitPart,
  data
}) {
  // If part validation is explicitly omitted, consider it as having an active part
  console.log("Checar!!!!! ");
  if (omitPart) {
    return true;
  }

  // Check if there's an active part on the workstation
  const hasActivePart =
    hasWorkstationActivePart(workstation) ||
    hasSelectedWorkstationActiveParts(workstationSelected);

  // If no active parts found, return false
  if (!hasActivePart) {
    return false;
  }

  // If the workstation uses manufacturing orders, verify there are active ones
  if (workstation?.use_manufacturing_orders) {
    return hasActiveManufacturingOrders(data);
  }

  // Workstation doesn't use manufacturing orders, so active part is sufficient
  return true;
}

/**
 * Check if the workstation has an active part assigned.
 */
function hasWorkstationActivePart(workstation) {
  return Boolean(workstation?.active_part?.id);
}

/**
 * Check if the selected workstation has any activated parts.
 */
function hasSelectedWorkstationActiveParts(workstationSelected) {
  return Boolean(workstationSelected?.activated_parts?.length);
}

/**
 * Check if there are active manufacturing orders in the data.
 */
function hasActiveManufacturingOrders(data) {
  return Boolean(data?.active_manufactoring_order?.length);
}

/**
 * Determine the menu-print-tags view based on company ID.
 */
function getMenuPrintTagsView(company) {
  if (IDS_TRAZABILITY_FLOW_ADVALTECH_PROD.includes(company?.id)) {
    return "advaltech";
  }
  if (IDS_TRAZABILITY_FLOW_BAFAR_PROD.includes(company?.id)) {
    return "bafar";
  }
  return "default";
}

/**
 * Pure function: given the dashboard state and company, returns the route
 * the user should be on. Returns null if no specific route is determined
 * (i.e., the user can stay where they are).
 *
 * This replicates the decision tree from useViewControl.
 */
export function resolveWorkstationRoute(dashboard, company) {
  const { workstationSelected, workstation, changePart, omitPart, data } =
    dashboard;

  if (!workstationSelected || !workstationSelected?.id) {
    return null; // handled by requireWorkstation
  }

  const hasActivePart = computeHasActivePart({
    workstationSelected,
    workstation,
    omitPart,
    data
  });

  const typeName = workstationSelected?.workstation_type?.type_name;
  const isTraceabilityType =
    typeName === "traceability" ||
    idsCompaniesTrazabilitySection.includes(company?.id);

  if (isTraceabilityType) {
    const workstationServices = workstationSelected?.workstation_services;
    const mainService = workstationServices?.length
      ? workstationServices[0]
      : null;

    if (mainService?.includes("create_traceability_order")) {
      return "dashboard-mo";
    }

    if (mainService?.includes("boarding_outgoing")) {
      if (changePart) return "select-parts";
      return "dashboard-tracking";
    }

    if (typeName === "checklist") {
      return "checklists";
    }

    const view = getMenuPrintTagsView(company);
    if (view == "bafar") {
      if (typeName === "productivity") {
        if (!hasActivePart || changePart) return "select-parts";
        return `menu-print-tags`;
      }
    }
    // if (!hasActivePart || changePart) return "select-parts";
    // return "dashboard";
    return "select-parts";
  }

  if (typeName === "checklist") {
    return "checklists";
  }

  // Default type
  if (!hasActivePart || changePart) return "select-parts";
  return "dashboard";
}

/**
 * beforeLoad guard: redirects to /workstations if no workstation is selected.
 * Use on all routes that require a workstation.
 */
export function requireWorkstation({ context, params }) {
  const { store } = context;
  const dashboard = getDashboardState(store);

  if (!dashboard.workstationSelected || !dashboard.workstationSelected?.id) {
    throw redirect({
      to: "/$lang/workstations",
      params: { lang: params.lang },
      search: { tab: "available" }
    });
  }
}

/**
 * beforeLoad guard for the /workstations route.
 * If a workstation IS selected, redirect to the appropriate page.
 */
export function redirectFromWorkstations({ context, params }) {
  const { store, queryClient } = context;
  const dashboard = getDashboardState(store);
  const company = getCompany(queryClient);

  if (dashboard.workstationSelected && dashboard.workstationSelected?.id) {
    const target = resolveWorkstationRoute(dashboard, company);
    if (target) {
      throw redirect({
        to: `/$lang/${target}`,
        params: { lang: params.lang }
      });
    }
  }
}

/**
 * beforeLoad guard for /select-parts route.
 * Checks if the user should actually be on a different page based on
 * workstation type and active part state.
 */
export function guardSelectParts({ context, params }) {
  const { store, queryClient } = context;
  const dashboard = getDashboardState(store);
  const company = getCompany(queryClient);

  if (!dashboard.workstationSelected || !dashboard.workstationSelected?.id) {
    throw redirect({
      to: "/$lang/workstations",
      params: { lang: params.lang },
      search: { tab: "available" }
    });
  }

  const target = resolveWorkstationRoute(dashboard, company);
  if (target && target !== "select-parts") {
    throw redirect({
      to: `/$lang/${target}`,
      params: { lang: params.lang }
    });
  }
}

/**
 * beforeLoad guard for /dashboard route.
 * Checks if the user should actually be on a different page based on
 * workstation type and active part state.
 */
export function guardDashboard({ context, params }) {
  const { store, queryClient } = context;
  const dashboard = getDashboardState(store);
  const company = getCompany(queryClient);


  if (!dashboard.workstationSelected || !dashboard.workstationSelected?.id) {
    throw redirect({
      to: "/$lang/workstations",
      params: { lang: params.lang },
      search: { tab: "available" }
    });
  }
}
